import { ILayerProps, IRenderLayerProps, LayerType } from '@/version-types';
import { ITextLayerProps } from './text-layer';

export type TableConstants = {
    readonly borderWidth: number;
    readonly borderColor: string;
    readonly cellWidth: number;
    readonly cellHeight: number;
};

export const commonTableConstants: TableConstants = {
    borderWidth: 2,
    borderColor: '#b4b8bfff',
    cellWidth: 218,
    cellHeight: 120,
};

// 由于IRenderLayerProps没做范型，导致类型不明确，这里做了适配，明确了具体的类型
export type ITableLayer<IsFormula = false> = Omit<IRenderLayerProps['layer'], 'layerType' | 'data' | '__runtime'> & {
    layerType: LayerType.TABLE;
    data: ITableLayerProps<IsFormula>;
} & (IsFormula extends false
        ? {
              __runtime?: ILayerProps['__runtime'] & {
                  /**
                   * 部分场景需要修复表格数据:
                   *  1. 部分浏览器在缩放等操作无法直接计算数据表格布局, 需要通过dom去获取最新数据
                   *  2. 从外部解析出来的表格数据布局大概率是错乱的 (比如xlsx解析为表格的时候, 解析出来的baseCells的值是不准确的). 但是最好用`TableLayer.fixTableLayout`去获取修复后的数据, 这样就不需要依赖于运行时的dom去修复了
                   */
                  shouldFixLayout?: boolean;
                  /** 是否禁用自动数据修复 (布局相关参数的取整) */
                  disableAutoDataFix?: boolean;
              };
          }
        : {});

export type PureCell = {
    /** 水平方向上的索引, 比如单元格左上角对应的是是第1列, 则hIndex就是0 */
    hIndex: number;
    /** 竖直方向上的索引, 比如单元格左上角对应的是是第1行, 则vIndex就是0 */
    vIndex: number;
    /** 单元格内容占用了多少列 */
    colSpan: number;
    /** 单元格内容占用了多少行 */
    rowSpan: number;
    content: string;
    styleIds: string[];
};
export type Cell<IsFormula = false> = PureCell &
    (IsFormula extends false
        ? // runtime的数据
          {
              /** 单元格唯一标识, 前缀带__render是因为拷贝图层时会自动删除(规范) */
              __renderId: string;
          }
        : {});

/**
 * 记录所有网格的信息:
 * 注: 左上角位置与单元格左上角位置相同, 也与colGroup相同, 不是表格左上角!!!
 */
export interface BaseCells {
    horizontal: number[];
    vertical: number[];
}

export type ITableLayerProps<IsFormula = false> = {
    /** 展示图片(移动端直接拿这个图片展示) */
    screenshotUrl?: string;
    /**
     * 所有单元格的默认样式, 该id不需要(可能会引发未知bug)写到Cell.styleIds数组中
     * 如果表格对应的preset有实现getCellDefaultStyle的话, 则该样式为getCellDefaultStyle返回的样式. 可以理解为allStyleId无法实现太复杂的默认样式(如preset8), 所以需要通过代码编程的方式来实现
     */
    allStyleId: string;
    cells: (Cell<IsFormula> | null)[][];
    baseCells: BaseCells;
    /** key为styleId, 必须是正整数数值 */
    styles: Record<string, CellStyle>;
    /** 记录该表格是由哪个预设创建的, 如果不是通过预设创建的则为空 */
    presetId?: string;
};

export type RowOrColSelectedRectInfo = {
    direction: 'horizontal' | 'vertical';
    index: number;
};

type TextStyle = Partial<
    Pick<
        ITextLayerProps,
        'fontSize' | 'textAlign' | 'color' | 'fontFamily' | 'bold' | 'italic' | 'lineThrough' | 'underline'
    > & {
        disableAutoWrap?: boolean;
    }
>;

export interface CellStyle extends TextStyle {
    verticalAlign?: 'top' | 'center' | 'bottom';
    backgroundColor?: `#${string}`;
    /** border-width为边框实际展示的宽度 (不是单元格内边框宽度) */
    border?: Record<'top' | 'left' | 'right' | 'bottom', Border>;
}

export type BorderStyle = 'line-0' | 'line-1' | 'line-2' | 'line-3' | 'line-4';

export interface Border {
    style: BorderStyle;
    width: number;
    color: `#${string}`;
}
